import React from 'react';
import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';
import Datetime from 'react-datetime';
import { isEqual } from 'lodash';

import { getAllTimezones } from 'services/timeZones.service';
import { canByLicensing } from 'utils/licensing/licensingChecker';
import { DEFAULT_PRE_CALL_NOTIFICATION } from 'utils/constants';

// @material-ui/core components
import withStyles from '@material-ui/core/styles/withStyles';
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete';

// core components
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import CustomInput from 'components/CustomInput/CustomInput';
import CustomTextField from 'components/CustomTextField/CustomTextField';
import FormControl from '@material-ui/core/FormControl';

const timeZones = getAllTimezones();

const style = {
	subTitle: {
		margin: '30px 0 0',
		color: '#aaa',
	},
	datetimeWrapper: {
		'& .rdtPicker': {
			top: '65px',
		},
	},
};

class AddContactCenterStepOne extends React.Component {
	constructor(props) {
		super(props);
		this.state = props?.allStates?.details || {};
	}

	componentDidMount() {
		this.checkLicensingParams();
	}

	checkLicensingParams = () => {
		const isAllowedDefaultNotificationByLicensing = canByLicensing('goodCalls.defaultNotifications');
		this.setState({ isAllowedDefaultNotificationByLicensing });

		if (!isAllowedDefaultNotificationByLicensing) {
			this.setState({ validVerbiage: DEFAULT_PRE_CALL_NOTIFICATION});
		}
	}

	sendState() {
		return this.state;
	}

	onFieldChange(key, value) {
		this.setState({
			[key]: value,
		});
	}

	async isValidated() {
		const { formMethods } = this.props;
		const { formState, trigger } = formMethods;
		await trigger();
		const { errors } = formState;
		return !Object.keys(errors).length;
	}

	render() {
		const { validation, formMethods } = this.props;
		const {
			contactCenterNameMaxLength,
			defaultNotificationMaxLength,
			unverifiedCallArrivalNotificationMaxLength,
		} = validation;
		const { control, trigger, formState, getValues } = formMethods;
		const { errors } = formState;
		const { isAllowedDefaultNotificationByLicensing} = this.state;

		return (
			<GridContainer justify="center">
				<GridItem xs={12} sm={12} md={6}>
					<Controller
						name="name"
						control={control}
						defaultValue={''}
						rules={{
							required: 'Field should not be empty',
							maxLength: {
								value: contactCenterNameMaxLength,
								message: `Maximum ${contactCenterNameMaxLength} characters`,
							},
						}}
						render={(props) => (
							<CustomInput
								labelText="Contact Center *"
								error={!!errors.name}
								helperText={
									errors.name ? errors.name.message : ''
								}
								formControlProps={{
									fullWidth: true,
								}}
								inputProps={{
									value: props.value,
									type: 'text',
								}}
								onChange={(event) => {
									const value = event.target.value?.trimStart();
									this.onFieldChange('name', value);
									props.onChange(value);
									trigger('name');
								}}
							/>
						)}
					/>
				</GridItem>
				<GridItem xs={12} sm={12} md={6}>
					<Controller
						name="validVerbiage"
						defaultValue={() => canByLicensing('goodCalls.defaultNotifications') ? '' : DEFAULT_PRE_CALL_NOTIFICATION}
						control={control}
						rules={{
							required: 'Field should not be empty',
							maxLength: {
								value: defaultNotificationMaxLength,
								message: `Maximum ${defaultNotificationMaxLength} characters`,
							},
						}}
						render={(props) => (
							<CustomInput
								labelText="Default Pre-Call Notification*"
								error={!!errors.validVerbiage}
								helperText={
									errors.validVerbiage
										? errors.validVerbiage.message
										: ''
								}
								formControlProps={{
									fullWidth: true,
									disabled: !isAllowedDefaultNotificationByLicensing,
								}}
								inputProps={{
									value: props.value,
									type: 'text',
								}}
								onChange={(event) => {
									const value = event.target.value?.trimStart();
									this.onFieldChange('validVerbiage', value);
									props.onChange(value);
									trigger('validVerbiage');
								}}
							/>
						)}
					/>
				</GridItem>
				<GridItem xs={12} sm={12} md={6}>
					<Controller
						name="fraudVerbiage"
						defaultValue={''}
						control={control}
						rules={{
							required: 'Field should not be empty',
							maxLength: {
								value: unverifiedCallArrivalNotificationMaxLength,
								message: `Maximum ${unverifiedCallArrivalNotificationMaxLength} characters`,
							},
						}}
						render={(props) => (
							<CustomInput
								labelText="Unverified Call Arrival Notification *"
								error={!!errors.fraudVerbiage}
								helperText={
									errors.fraudVerbiage
										? errors.fraudVerbiage.message
										: ''
								}
								formControlProps={{
									fullWidth: true,
								}}
								inputProps={{
									value: props.value,
									type: 'text',
								}}
								onChange={(event) => {
									const value = event.target.value?.trimStart();
									this.onFieldChange('fraudVerbiage', value);
									props.onChange(value);
									trigger('fraudVerbiage');
								}}
							/>
						)}
					/>
				</GridItem>
				<GridItem xs={12} sm={12} md={6}>
				</GridItem>
				<GridItem md={12}>
					<h4 className={this.props.classes.subTitle}>Work Hours</h4>
				</GridItem>
				<GridItem xs={12} sm={12} md={6}>
					<Controller
						name="startOfWork"
						control={control}
						defaultValue={null}
						rules={{
							required: 'Field should not be empty',
							validate: () => {
								const { endOfWork, startOfWork } = getValues();

								if (isEqual(endOfWork, startOfWork)) {
									return 'Start time cannot be same as the End time'
								}
							}
						}}
						render={({ onChange, value }) => (
							<FormControl fullWidth>
								<div className={this.props.classes.datetimeWrapper}>
									<Datetime
										renderInput={(props) =>
											<CustomTextField {...props}
												error={!!errors.startOfWork}
												helperText={errors.startOfWork ? errors.startOfWork.message : ""}
												label="Start Time *"
												margin="normal"
												inputProps={{ readOnly: true }}
											/>}
										value={value}
										timeConstraints={{
											minutes: {
												step: 15
											},
										}}
										dateFormat={false}
										inputProps={{ fullWidth: true }}
										onChange={({ _d }) => {
											const hour = new Date(_d).getHours();
											const minute = new Date(_d).getMinutes();
											onChange({ hour, minute });
											this.onFieldChange('startOfWork', { hour, minute });
											trigger('startOfWork');
										}}
									/>
								</div>
							</FormControl>
						)}
					/>
				</GridItem>
				<GridItem xs={12} sm={12} md={6}>
					<Controller
						name="endOfWork"
						control={control}
						defaultValue={null}
						rules={{
							required: 'Field should not be empty',
							validate: () => {
								const { endOfWork, startOfWork } = getValues();

								if (isEqual(endOfWork, startOfWork)) {
									return 'Start time cannot be same as the End time'
								}
							}
						}}
						render={({ onChange, value }) => (
							<FormControl fullWidth>
								<div className={this.props.classes.datetimeWrapper}>
									<Datetime
										renderInput={(props) =>
											<CustomTextField {...props}
												error={!!errors.endOfWork}
												helperText={errors.endOfWork ? errors.endOfWork.message : ""}
												label="End Time *"
												margin="normal"
												inputProps={{ readOnly: true }}
											/>}
										value={value}
										timeConstraints={{
											minutes: {
												step: 15
											},
										}}
										dateFormat={false}
										inputProps={{ fullWidth: true }}
										onChange={({ _d }) => {
											const hour = new Date(_d).getHours();
											const minute = new Date(_d).getMinutes();
											onChange({ hour, minute });
											trigger('endOfWork');
											this.onFieldChange('endOfWork', { hour, minute });
										}}
									/>
								</div>
							</FormControl>
						)}
					/>
				</GridItem>
				<GridItem xs={12} sm={12} md={6}>
					<Controller
						name="timeZone"
						control={control}
						defaultValue={''}
						rules={{
							required: 'Field should not be empty',
						}}
						render={({ onChange, value }) => (
							<Autocomplete
								options={timeZones}
								autoComplete
								includeInputInList
								value={value}
								filterOptions={createFilterOptions({ trim: true })}
								renderInput={(params) =>
									<CustomTextField {...params}
										error={!!errors.timeZone}
										helperText={errors.timeZone ? errors.timeZone.message : ""}
										label="Time Zone *"
										margin="normal" />
								}
								onChange={(e, val) => {
									onChange(val);
									this.onFieldChange('timeZone', val);
									trigger('timeZone');
								}}
								onBlur={() => {
									trigger('timeZone');
								}}
							/>
						)}
					/>
				</GridItem>
				<GridItem md={6} />
			</GridContainer>
		);
	}
}

AddContactCenterStepOne.propTypes = {
	classes: PropTypes.object,
	formMethods: PropTypes.any,
};

export default withStyles(style)(AddContactCenterStepOne);
